import { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const CONTAINER_ID = "kodelr-dashboard-webchat-script";

const pathPageMap = [
  { page: "dashboard", matches: pathname => pathname === "/" },
  { page: "connections", matches: pathname => pathname === "/connections" },
  { page: "tickets", matches: pathname => pathname.startsWith("/tickets") },
  { page: "contacts", matches: pathname => pathname === "/contacts" },
  { page: "quickAnswers", matches: pathname => pathname === "/quickAnswers" },
  { page: "reminders", matches: pathname => pathname === "/reminders" },
  { page: "agenda", matches: pathname => pathname === "/appointments" },
  { page: "orders", matches: pathname => pathname === "/orders" },
  { page: "streaming", matches: pathname => pathname === "/streaming" },
  { page: "internalChat", matches: pathname => pathname === "/internal-chat" },
  { page: "funnels", matches: pathname => pathname === "/funnels" },
  { page: "chatbot", matches: pathname => pathname === "/chatbot" },
  { page: "ai", matches: pathname => pathname === "/ai" },
  { page: "users", matches: pathname => pathname === "/users" },
  { page: "queues", matches: pathname => pathname.toLowerCase() === "/queues" },
  { page: "labels", matches: pathname => pathname === "/labels" }
];

const getPageFromPath = pathname =>
  pathPageMap.find(item => item.matches(pathname))?.page || "";

const parseAllowedPages = value => {
  if (!value) return ["dashboard"];

  try {
    const pages = JSON.parse(value);
    return Array.isArray(pages) ? pages : ["dashboard"];
  } catch {
    return ["dashboard"];
  }
};

const cleanupWidget = container => {
  if (window.__KODELR_WEBCHAT_INSTANCE__?.cleanup) {
    window.__KODELR_WEBCHAT_INSTANCE__.cleanup();
  }
  if (container) container.remove();
  const widgetRoot = document.getElementById("kodelr-webchat-root");
  if (widgetRoot) widgetRoot.remove();
  document
    .querySelectorAll(".kodelr-image-modal")
    .forEach(imageModal => imageModal.remove());
  const previousContainer = document.getElementById(CONTAINER_ID);
  if (previousContainer) previousContainer.remove();
  delete window.KODELR_WEBCHAT_USER;
};

const applyWidgetOptions = options => {
  if (!window.KODELR_WEBCHAT) return;

  window.KODELR_WEBCHAT.config = {
    ...(window.KODELR_WEBCHAT.config || {}),
    ...options,
  };
};

const appendExecutableScript = (scriptElement, container, options = {}) => {
  const executableScript = document.createElement("script");

  Array.from(scriptElement.attributes).forEach(attribute => {
    executableScript.setAttribute(attribute.name, attribute.value);
  });

  executableScript.text = scriptElement.text || scriptElement.textContent || "";
  container.appendChild(executableScript);

  if (!scriptElement.src) {
    applyWidgetOptions(options);
  }
};

const appendSnippet = (snippet, options = {}) => {
  const container = document.createElement("div");
  container.id = CONTAINER_ID;
  container.style.display = "none";
  document.body.appendChild(container);

  if (!/<[a-z][\s\S]*>/i.test(snippet)) {
    const script = document.createElement("script");
    script.text = snippet;
    container.appendChild(script);
    applyWidgetOptions(options);
    return container;
  }

  const template = document.createElement("template");
  template.innerHTML = snippet;

  Array.from(template.content.childNodes).forEach(node => {
    if (node.nodeName.toLowerCase() === "script") {
      appendExecutableScript(node, container, options);
      return;
    }

    container.appendChild(node.cloneNode(true));
  });

  return container;
};

const DashboardWebChatScript = () => {
  const { user, isAuth } = useContext(AuthContext);
  const location = useLocation();
  const [settingsVersion, setSettingsVersion] = useState(0);

  useEffect(() => {
    const handleSettingsChanged = () => {
      setSettingsVersion(version => version + 1);
    };

    window.addEventListener(
      "dashboardWebChatSettingsChanged",
      handleSettingsChanged
    );

    return () => {
      window.removeEventListener(
        "dashboardWebChatSettingsChanged",
        handleSettingsChanged
      );
    };
  }, []);

  useEffect(() => {
    let mounted = true;
    let container = null;

    const loadScript = async () => {
      if (!isAuth || !user?.id) return;

      try {
        const { data } = await api.get("/settings/dashboard/webchat-script");
        const snippet = (data?.value || "").trim();
        const launcherMode = data?.launcherMode === "header" ? "header" : "bubble";
        const currentPage = getPageFromPath(location.pathname);
        const allowedPages = parseAllowedPages(data?.pages);
        if (
          launcherMode !== "header" &&
          (!currentPage || !allowedPages.includes(currentPage))
        ) {
          cleanupWidget(container);
          return;
        }
        if (!mounted || !snippet) return;

        const previousContainer = document.getElementById(CONTAINER_ID);
        if (previousContainer) previousContainer.remove();

        window.KODELR_WEBCHAT_USER = {
          id: user.id,
          name: user.name || "",
          email: user.email || "",
        };

        container = appendSnippet(snippet, { launcherMode });
      } catch {
        // The dashboard should keep working even if the optional widget fails.
      }
    };

    loadScript();

    return () => {
      mounted = false;
      cleanupWidget(container);
    };
  }, [isAuth, user?.id, user?.name, user?.email, location.pathname, settingsVersion]);

  return null;
};

export default DashboardWebChatScript;
